'use client';

import { Info } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { useMemo } from 'react';

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';

import { Tooltip } from '@/shared/components';

import { cn } from '@/lib/utils';

import { LockedOverlay } from './locked-overlay';
import { MultiAreaChartCardProps, MultiAreaDataPoint } from './types';

// Dummy data for locked/preview state
const DUMMY_MULTI_CHART_DATA: MultiAreaDataPoint[] = [
	{ label: 'Jan', value1: 45, value2: 38 },
	{ label: 'Feb', value1: 52, value2: 45 },
	{ label: 'Mar', value1: 48, value2: 42 },
	{ label: 'Apr', value1: 65, value2: 58 },
	{ label: 'May', value1: 58, value2: 52 },
	{ label: 'Jun', value1: 70, value2: 63 },
	{ label: 'Jul', value1: 75, value2: 68 },
];

/**
 * Reusable multi-series area chart card component
 * Used for displaying multiple data series on the same chart (e.g., views & submissions)
 */
export function MultiAreaChartCard({
	title,
	helperText,
	data,
	series,
	isLocked = false,
	isLoading = false,
	showLegend = true,
	showYAxis = false,
	showXAxis = false,
	valueFormatter,
	height = 'h-48',
	className,
	filterEmptyDataEntries = false,
}: MultiAreaChartCardProps) {
	// Use dummy data when locked to avoid exposing real data in DOM
	// Optionally filter out entries where all series values are zero
	const displayData = useMemo(() => {
		if (isLocked) {
			// Map dummy data to use the actual series keys
			return DUMMY_MULTI_CHART_DATA.map((point, index) => ({
				label: point.label,
				...series.reduce(
					(acc, s, i) => ({
						...acc,
						[s.dataKey]: DUMMY_MULTI_CHART_DATA[index]?.[`value${i + 1}`] ?? 0,
					}),
					{}
				),
			}));
		}
		if (!filterEmptyDataEntries || !data) return data;
		// Filter out entries where all series values are zero/null/undefined
		return data.filter((point) =>
			series.some((s) => {
				const val = point[s.dataKey];
				return val !== 0 && val !== null && val !== undefined;
			})
		);
	}, [isLocked, data, series, filterEmptyDataEntries]);

	const chartConfig = useMemo(
		() =>
			series.reduce(
				(acc, s) => ({
					...acc,
					[s.dataKey]: {
						label: s.label,
						color: s.color,
					},
				}),
				{}
			),
		[series]
	);

	// Generate unique gradient IDs to avoid conflicts
	const gradientIds = useMemo(
		() =>
			series.map((s) => ({
				key: s.dataKey,
				id: `fill-${s.dataKey}-${Math.random().toString(36).slice(2, 9)}`,
			})),
		[series]
	);

	if (isLoading) {
		return (
			<div className={cn('rounded-lg border border-border bg-card p-4', className)}>
				<Skeleton className="mb-4 h-5 w-32" />
				<Skeleton className={cn('w-full', height)} />
			</div>
		);
	}

	return (
		<div className={cn('rounded-lg border border-border bg-card p-4', className)}>
			{/* Header */}
			{title && (
				<div className="mb-4 flex items-center gap-1">
					<h3 className="font-medium">{title}</h3>
					{helperText && (
						<Tooltip text={helperText}>
							<Info className="h-3.5 w-3.5 cursor-default text-muted-foreground/70 transition-colors hover:text-muted-foreground" />
						</Tooltip>
					)}
				</div>
			)}

			{/* Chart - Wrapped with locked overlay */}
			<LockedOverlay isLocked={isLocked}>
				{displayData?.length > 0 ? (
					<ChartContainer
						config={chartConfig}
						className={cn('[&]:aspect-auto', height)}
					>
						<AreaChart
							data={displayData}
							margin={{ top: 5, right: 0, bottom: 0, left: showYAxis ? 12 : 0 }}
						>
							<defs>
								{series.map((s, index) => {
									const gradientId = gradientIds[index].id;
									return (
										<linearGradient
											key={s.dataKey}
											id={gradientId}
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="5%"
												stopColor={s.color}
												stopOpacity={0.8}
											/>
											<stop
												offset="95%"
												stopColor={s.color}
												stopOpacity={0.1}
											/>
										</linearGradient>
									);
								})}
							</defs>
							{(showXAxis || showYAxis) && (
								<CartesianGrid
									strokeDasharray="3 3"
									vertical={false}
								/>
							)}
							{showXAxis && (
								<XAxis
									dataKey="label"
									tickLine={false}
									axisLine={false}
									tickMargin={8}
									tick={{ fontSize: 10 }}
									interval="preserveStartEnd"
								/>
							)}
							{showYAxis && (
								<YAxis
									tickLine={false}
									axisLine={false}
									tick={{ fontSize: 10 }}
									width={30}
									tickFormatter={valueFormatter}
								/>
							)}
							<ChartTooltip
								cursor={false}
								content={
									<ChartTooltipContent
										indicator="dot"
										labelFormatter={(_, payload) => {
											// Show date range in tooltip if available
											const labelRange = payload?.[0]?.payload?.labelRange;
											const label = payload?.[0]?.payload?.label;
											return labelRange ?? label ?? '-';
										}}
									/>
								}
							/>
							{series.map((s, index) => {
								const gradientId = gradientIds[index].id;
								return (
									<Area
										key={s.dataKey}
										type="monotone"
										dataKey={s.dataKey}
										stroke={s.color}
										fill={`url(#${gradientId})`}
										fillOpacity={0.4}
										strokeWidth={2}
									/>
								);
							})}
							{showLegend && <ChartLegend content={<ChartLegendContent />} />}
						</AreaChart>
					</ChartContainer>
				) : (
					<div
						className={cn(
							'flex w-full items-center justify-center text-sm text-muted-foreground',
							height
						)}
					>
						No data available
					</div>
				)}
			</LockedOverlay>
		</div>
	);
}
